// Content Security Policy — per-site configuration + violation feed.
//
// v2 layout: V2PageHeader → three SectionCards:
//   1. Protection status (3 tiles: mode, violations 24h, top directive)
//   2. Policy configuration (mode picker + enabled toggle + policy
//      textarea + rate-cap + report token + save/discard)
//   3. Recent violations (table with per-row drill-down drawer,
//      backed by /config/csp/:site_uid/violations)
//
// Palette + primitives borrowed from DDosMigrationConfig so the two
// service-config pages read as one visual family.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Alert,
  Select,
  MenuItem,
  Menu,
} from "@mui/material";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import { useNotification } from "../../../../../contexts/NotificationContext";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import StickySaveBar from "../../../../common/StickySaveBar";
import { GreenSwitch } from "../../../../common/Greens";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import { PrimaryButton } from "../../../../common/Button";
import DeleteConfirmModal from "../../../../common/DeleteConfirmModal";
import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import { generateMenuSx } from "../../../../../layouts/_new/Normal";
import colors from "../../../../../utils/colors";
import { AsecModeSegmented } from "../api_security/components/asecPrimitives";

import useCspConfig from "../../../../../hooks/user/useCspConfig";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";

// ── palette tokens (kept in sync with DDoS / BM pages) ─────────
const INDIGO = colors.primary[300];
const INDIGO_INK = "#454FAD";
const INDIGO_BG = "rgba(99, 113, 247, 0.08)";
const BORDER = colors.neutral[200];
const FG1 = colors.neutral[1000];
const FG2 = colors.neutral[700];
const FG3 = colors.neutral[500];
const SUCCESS_BG = "#E8FBEF";
const SUCCESS_FG = "#0f7a42";
const SUCCESS_BORDER = "#C8E6D4";
const AMBER_BG = "#FFF6D0";
const AMBER_FG = "#7a5b10";
const AMBER_BORDER = "#F2DE9A";
const DANGER_BG = "#FEECEE";
const DANGER_FG = "#A11228";
const DANGER_BORDER = "#F4C7CD";
const NEUTRAL_BG = colors.neutral[100];
const NEUTRAL_BORDER = BORDER;

/** Indigo outlined action — wins over MUI theme hover greying. */
const cspOutlinedButtonSx = {
  height: 36,
  px: 1.75,
  borderRadius: "8px",
  textTransform: "none",
  fontSize: 13,
  fontWeight: 600,
  border: `1px solid ${INDIGO}`,
  color: INDIGO,
  backgroundColor: "#fff",
  boxShadow: "none",
  "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "inherit" },
  "&&:hover": {
    borderColor: INDIGO_INK,
    color: INDIGO_INK,
    backgroundColor: INDIGO_BG,
    boxShadow: "none",
  },
  "&&:active": {
    borderColor: INDIGO_INK,
    color: INDIGO_INK,
    backgroundColor: "rgba(99, 113, 247, 0.10)",
    boxShadow: "none",
  },
};

/** Token / utility icon buttons next to the report-token field. */
const CSP_TOKEN_CONTROL_H = 40;
const cspIconButtonSx = {
  width: CSP_TOKEN_CONTROL_H,
  height: CSP_TOKEN_CONTROL_H,
  minWidth: CSP_TOKEN_CONTROL_H,
  flexShrink: 0,
  borderRadius: "8px",
  border: `1px solid ${BORDER}`,
  color: FG2,
  backgroundColor: "#fff",
  p: 0,
  "&:hover": {
    borderColor: INDIGO,
    backgroundColor: INDIGO_BG,
    color: INDIGO,
  },
};

const MODES = [
  {
    key: "off",
    label: "Off",
    desc: "No header injected. Zero impact on traffic.",
    Icon: BlockOutlinedIcon,
    tone: { bg: NEUTRAL_BG, fg: FG2, br: NEUTRAL_BORDER },
  },
  {
    key: "discovery",
    label: "Discovery",
    desc: "Layerd ships a maximally strict report-only policy so every subresource fires a violation. Use it to learn your site's real script inventory before authoring a policy.",
    Icon: ExploreOutlinedIcon,
    tone: { bg: "rgba(99, 113, 247, 0.10)", fg: INDIGO, br: "rgba(99, 113, 247, 0.28)" },
  },
  {
    key: "report_only",
    label: "Report only",
    desc: "Browsers report violations but do not block. Safe rollout mode for the policy you've authored.",
    Icon: BugReportOutlinedIcon,
    tone: { bg: AMBER_BG, fg: AMBER_FG, br: AMBER_BORDER },
  },
  {
    key: "enforce",
    label: "Enforce",
    desc: "Browsers block matching violations AND report. Live protection.",
    Icon: ShieldOutlinedIcon,
    tone: { bg: SUCCESS_BG, fg: SUCCESS_FG, br: SUCCESS_BORDER },
  },
];

function fmtNumber(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString();
}

function fmtWhen(ts) {
  if (!ts) return "—";
  const d = new Date(Number(ts));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function shortenUri(u, max = 60) {
  if (!u) return "—";
  if (u.length <= max) return u;
  return u.slice(0, max - 1) + "…";
}

// ── SectionCard: mirrors DDoS / BM Config primitive ─────────────
function SectionCard({ icon, title, desc, right, children, bodyPad = "18px 20px" }) {
  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.75,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "9px",
                background: INDIGO_BG,
                color: INDIGO,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { width: 18, height: 18 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 16, color: FG1, mb: 0.25 }}>
              {title}
            </Typography>
            {desc && (
              <Typography sx={{ fontSize: 12.5, color: FG2, lineHeight: 1.5, maxWidth: 640 }}>
                {desc}
              </Typography>
            )}
          </Box>
        </Box>
        {right}
      </Box>
      {children !== undefined && <Box sx={{ padding: bodyPad }}>{children}</Box>}
    </Box>
  );
}

function StatusTile({ Icon, label, value, sub, accent }) {
  return (
    <Box
      sx={{
        padding: "14px 16px",
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        background: accent
          ? "linear-gradient(180deg, rgba(99, 113, 247, 0.06) 0%, rgba(99, 113, 247, 0) 60%)"
          : "#fff",
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "6px",
            background: NEUTRAL_BG,
            color: FG2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { width: 14, height: 14 },
          }}
        >
          <Icon />
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: FG2,
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: FG1, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: FG3, mt: 0.5 }}>{sub}</Typography>
      )}
    </Box>
  );
}

function ModePickerRow({ current, onPick, disabled }) {
  const options = MODES.map(({ key, label, Icon }) => ({
    value: key,
    label,
    icon: <Icon sx={{ fontSize: 14, color: "inherit" }} />,
    tone: key === "off" ? "off" : key === "enforce" ? "enforce" : key === "report_only" ? "warn" : "info",
  }));

  return (
    <Stack spacing={1}>
      <AsecModeSegmented
        value={current}
        onChange={onPick}
        options={options}
        disabled={disabled}
      />
      <Typography sx={{ fontSize: 12, color: FG2, lineHeight: 1.45 }}>
        {MODES.find((m) => m.key === current)?.desc}
      </Typography>
    </Stack>
  );
}

function ViolationDrawer({ open, row, onClose, onOpenInInventory }) {
  if (!row) return null;
  const rows = [
    ["Time", fmtWhen(row.ts)],
    ["Mode", row.mode],
    ["Directive", row.effective_directive || row.violated_directive || "—"],
    ["Blocked URI", row.blocked_uri || "—"],
    ["Document URI", row.document_uri || "—"],
    ["Source file", row.source_file || "—"],
    ["Line / Column", row.line_number || row.column_number
      ? `${row.line_number || 0} : ${row.column_number || 0}`
      : "—"],
    ["Status code", row.status_code || "—"],
    ["Referrer", row.referrer || "—"],
    ["Source IP", row.src_ip || "—"],
    ["Report format", row.source_format || "—"],
    ["User agent", row.user_agent || "—"],
  ];
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 520 } } }}
    >
      <Box sx={{ padding: "20px 22px" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: FG1 }}>
            Violation detail
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack spacing={1.25}>
          {rows.map(([k, v]) => (
            <Box key={k}>
              <Typography sx={{ fontSize: 11, color: FG3, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em" }}>
                {k}
              </Typography>
              <Typography sx={{ fontSize: 13, color: FG1, wordBreak: "break-all", mt: 0.25 }}>
                {v}
              </Typography>
            </Box>
          ))}
        </Stack>
        {row.blocked_uri && onOpenInInventory && (
          <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${BORDER}` }}>
            <Button
              fullWidth
              variant="outlined"
              disableElevation
              startIcon={<OpenInNewOutlinedIcon fontSize="small" />}
              onClick={() => onOpenInInventory(row.blocked_uri)}
              sx={cspOutlinedButtonSx}
            >
              View this script in Inventory
            </Button>
            <Typography sx={{ fontSize: 11.5, color: FG3, mt: 1, textAlign: "center" }}>
              Cross-reference the blocked URI against the authorized script catalogue.
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// Banner shown at the top of the page when the site is currently in
// discovery mode. Gives the operator a one-glance answer to
// "how long has this been running", "what has it caught", and
// "how do I end it" without hunting through the policy card.
function DiscoveryActiveBanner({ startedAt, violationCount, directiveCount, onEndDiscovery, onGeneratePolicy }) {
  const started = startedAt ? new Date(startedAt) : null;
  const elapsed = started ? Math.max(0, Date.now() - started.getTime()) : 0;
  const days = Math.floor(elapsed / (24 * 3600 * 1000));
  const hours = Math.floor((elapsed % (24 * 3600 * 1000)) / (3600 * 1000));
  const elapsedLabel =
    !started
      ? "just started"
      : days > 0
      ? `${days}d ${hours}h ago`
      : hours > 0
      ? `${hours}h ago`
      : "just now";
  return (
    <Box
      sx={{
        border: `1px solid rgba(99, 113, 247, 0.28)`,
        background: "rgba(99, 113, 247, 0.06)",
        borderRadius: "14px",
        padding: "18px 20px",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            background: "rgba(99, 113, 247, 0.14)",
            color: INDIGO,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { width: 22, height: 22 },
            flexShrink: 0,
          }}
        >
          <ExploreOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: FG1 }}>
              Discovery active
            </Typography>
            <Chip
              size="small"
              label={`Started ${elapsedLabel}`}
              sx={{ height: 20, fontSize: 11, background: "rgba(99, 113, 247, 0.14)", color: INDIGO, fontWeight: 600 }}
            />
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: FG2, lineHeight: 1.5 }}>
            Layerd is shipping the maximally strict report-only policy so every subresource fires a browser violation. So far we've captured{" "}
            <b>{fmtNumber(violationCount)}</b> violation{violationCount === 1 ? "" : "s"} across{" "}
            <b>{directiveCount}</b> directive{directiveCount === 1 ? "" : "s"}. Let it run long enough to cover the least-active pages you care about (a full weekly business cycle is common), then end discovery to review the candidate.
          </Typography>
        </Box>
        <Stack direction={{ xs: "row", md: "row" }} spacing={1} alignItems="center">
          <Button
            onClick={onGeneratePolicy}
            size="small"
            variant="outlined"
            disableElevation
            startIcon={<AutoAwesomeOutlinedIcon />}
            sx={cspOutlinedButtonSx}
          >
            Generate policy
          </Button>
          <PrimaryButton onClick={onEndDiscovery} size="small">
            End discovery
          </PrimaryButton>
        </Stack>
      </Stack>
    </Box>
  );
}

// Phase 6.3 — Route override editor. Small controlled-form dialog
// shared by Add and Edit. Client-side validation mirrors the CP
// Phase 5 — Violation intelligence panel. Renders the four intel
// artefacts (time-series sparkline, top-violator clusters,
// source-file attribution, campaigns) into a single scannable
// SectionCard body. Purely presentational — the parent owns the
// window-hours switch and the fetch state.
function ViolationIntelPanel({ data, loading }) {
  const kindMeta = {
    "traffic-wide": {
      label: "Traffic-wide",
      bg: DANGER_BG,
      fg: DANGER_FG,
      br: DANGER_BORDER,
      hint: "Many users see this — likely a real deployment issue.",
    },
    "widely-embedded": {
      label: "Widely embedded",
      bg: AMBER_BG,
      fg: AMBER_FG,
      br: AMBER_BORDER,
      hint: "Same resource blocked across many pages — usually a shared header/footer include.",
    },
    burst: {
      label: "Burst",
      bg: NEUTRAL_BG,
      fg: FG2,
      br: BORDER,
      hint: "Concentrated on one page or one client — bot, tool, or single-page issue.",
    },
  };

  if (loading && !data) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={22} sx={{ color: INDIGO }} />
      </Stack>
    );
  }
  if (!data || data.total_count === 0) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Typography sx={{ fontSize: 13, color: FG2 }}>
          No violation reports in this window. Either the site is quiet, the policy is clean, or CSP is set to Off.
        </Typography>
      </Box>
    );
  }

  // Sparkline: max value scales the bar heights.
  const series = data.time_series || [];
  const maxVal = Math.max(1, ...series.map((s) => s.count || 0));

  return (
    <Stack spacing={3}>
      {/* Time series sparkline */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <InsightsOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Volume over window · {fmtNumber(data.total_count)} reports total
          </Typography>
        </Stack>
        {series.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: FG3 }}>No data in window.</Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "3px", height: 60, background: NEUTRAL_BG, borderRadius: "8px", padding: "6px 8px", border: `1px solid ${BORDER}` }}>
            {series.map((s, i) => {
              const h = Math.max(2, Math.round(((s.count || 0) / maxVal) * 48));
              return (
                <Tooltip key={i} title={`${new Date(s.ts_ms).toLocaleString()} · ${fmtNumber(s.count)} reports`}>
                  <Box sx={{ flex: 1, height: h, background: INDIGO, borderRadius: "2px", opacity: s.count > 0 ? 0.85 : 0.2 }} />
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Campaigns */}
      {data.campaigns && data.campaigns.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
            <GroupsOutlinedIcon sx={{ fontSize: 15, color: DANGER_FG }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: DANGER_FG, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Campaigns detected · {data.campaigns.length}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: FG3, mb: 1 }}>
            Clusters hitting ≥3× across ≥3 distinct IPs or pages. Investigate widely-embedded and traffic-wide ones first — one-off bursts are usually low priority.
          </Typography>
          <Stack spacing={0.75}>
            {data.campaigns.map((c, i) => {
              const km = kindMeta[c.kind] || kindMeta.burst;
              return (
                <Box key={i} sx={{ padding: "10px 12px", border: `1px solid ${km.br}`, borderRadius: "10px", background: km.bg }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Chip
                      label={km.label}
                      size="small"
                      sx={{ fontSize: 11, height: 20, fontWeight: 700, background: "#fff", color: km.fg, border: `1px solid ${km.br}` }}
                    />
                    <Typography sx={{ fontSize: 12.5, color: km.fg, fontFamily: "ui-monospace, SFMono-Regular, monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <b>{c.violated_directive}</b> → {shortenUri(c.blocked_uri, 90)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: km.fg }}>
                      {fmtNumber(c.count)}×
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                    <Typography sx={{ fontSize: 11, color: km.fg }}>
                      {c.distinct_ips} distinct IP{c.distinct_ips === 1 ? "" : "s"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: km.fg }}>
                      {c.distinct_source_files} distinct page{c.distinct_source_files === 1 ? "" : "s"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: km.fg }}>
                      First seen {fmtWhen(c.first_seen_ms)}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: km.fg, mt: 0.5, fontStyle: "italic" }}>
                    {km.hint}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Top violators */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <ReportProblemOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Top violators · top {data.top_violators?.length || 0}
          </Typography>
        </Stack>
        {data.top_violators && data.top_violators.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Directive</TableCell>
                <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Blocked URI</TableCell>
                <TableCell align="right" sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Count</TableCell>
                <TableCell align="right" sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>IPs</TableCell>
                <TableCell align="right" sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Pages</TableCell>
                <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Last seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.top_violators.map((v, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontSize: 12.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                    {v.violated_directive || "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace", maxWidth: 340, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <Tooltip title={v.blocked_uri || ""}>
                      <span>{shortenUri(v.blocked_uri, 60)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, color: FG1 }}>{fmtNumber(v.count)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, color: FG2 }}>{fmtNumber(v.distinct_ips)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, color: FG2 }}>{fmtNumber(v.distinct_source_files)}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: FG2, whiteSpace: "nowrap" }}>{fmtWhen(v.last_seen_ms)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography sx={{ fontSize: 12, color: FG3 }}>No clusters in window.</Typography>
        )}
      </Box>

      {/* Source-file attribution */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Top violating pages · attribution
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: FG3, mb: 1 }}>
          Which page templates are generating violations. High <b>distinct URIs</b> on one page suggests a widely-embedded include (shared header, analytics bundle); low <b>distinct URIs</b> = a single stubborn resource.
        </Typography>
        {data.by_source_file && data.by_source_file.length > 0 ? (() => {
          // Follow-up B — attribute % share of total. Without this
          // context, a source-file table where one page contributes
          // 90/100 violations looks the same on the eye as 3/100 —
          // the operator can't tell which is a "burning problem"
          // vs "background noise." A share column + a visual bar
          // fixes that at a glance.
          const totalForShare = data.by_source_file.reduce(
            (s, f) => s + (Number(f.count) || 0),
            0
          );
          const topShare = totalForShare > 0
            ? (Number(data.by_source_file[0].count) || 0) / totalForShare
            : 0;
          return (
            <>
              {topShare >= 0.6 && (
                <Typography sx={{ fontSize: 11.5, color: AMBER_FG, mb: 1, fontStyle: "italic" }}>
                  ⚠ One page accounts for {(topShare * 100).toFixed(0)}% of the violations shown — investigate it first; the others are likely correlated noise from shared includes.
                </Typography>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Source page</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Count</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", width: 140 }}>Share</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Distinct URIs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.by_source_file.map((f, i) => {
                    const share = totalForShare > 0 ? (Number(f.count) || 0) / totalForShare : 0;
                    const pctStr = (share * 100).toFixed(share < 0.01 ? 2 : 1);
                    return (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: 12.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace", maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Tooltip title={f.source_file || ""}>
                            <span>{shortenUri(f.source_file, 62)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, color: FG1 }}>{fmtNumber(f.count)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                            {/* Inline bar — width proportional to share.
                                Height is small so it doesn't dominate the row. */}
                            <Box sx={{ width: 60, height: 8, background: NEUTRAL_BG, border: `1px solid ${BORDER}`, borderRadius: "4px", overflow: "hidden" }}>
                              <Box
                                sx={{
                                  width: `${Math.max(2, share * 100)}%`,
                                  height: "100%",
                                  background: share >= 0.5 ? DANGER_FG : share >= 0.2 ? AMBER_FG : INDIGO,
                                  opacity: 0.85,
                                }}
                              />
                            </Box>
                            <Typography sx={{ fontSize: 12, color: FG2, fontVariantNumeric: "tabular-nums", minWidth: 42, textAlign: "right" }}>
                              {pctStr}%
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, color: FG2 }}>{fmtNumber(f.distinct_blocked_uris)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          );
        })() : (
          <Typography sx={{ fontSize: 12, color: FG3 }}>No source-file data in window (older reports didn't include it).</Typography>
        )}
      </Box>
    </Stack>
  );
}

// Phase 2.3 — coverage gauge + top-blocked chips. Rendered inside
// the GeneratePolicyDialog once the simulate endpoint returns.
function CoveragePreview({ simulation, simulating }) {
  if (simulating && !simulation) {
    return (
      <Box
        sx={{
          padding: "14px 16px",
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          background: NEUTRAL_BG,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={18} sx={{ color: INDIGO }} />
          <Typography sx={{ fontSize: 12.5, color: FG2 }}>
            Replaying candidate against historical violations…
          </Typography>
        </Stack>
      </Box>
    );
  }
  if (!simulation) return null;
  const pct = Number(simulation.coverage_pct || 0);
  const tone =
    pct >= 95 ? { bg: SUCCESS_BG, fg: SUCCESS_FG, br: SUCCESS_BORDER, label: "Ready to promote" } :
    pct >= 80 ? { bg: AMBER_BG, fg: AMBER_FG, br: AMBER_BORDER, label: "Nearly there" } :
                { bg: DANGER_BG, fg: DANGER_FG, br: DANGER_BORDER, label: "Needs refinement" };
  const barBg = pct >= 95 ? SUCCESS_FG : pct >= 80 ? AMBER_FG : DANGER_FG;
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Coverage against last {simulation.window_hours}h
        </Typography>
        <Chip
          size="small"
          label={tone.label}
          sx={{ fontSize: 11, height: 20, background: tone.bg, color: tone.fg, border: `1px solid ${tone.br}`, fontWeight: 600 }}
        />
      </Stack>
      <Box
        sx={{
          padding: "16px 18px",
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          background: "#fff",
        }}
      >
        <Stack direction="row" spacing={2.5} alignItems="baseline">
          <Typography sx={{ fontSize: 32, fontWeight: 800, color: FG1, lineHeight: 1 }}>
            {pct}%
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: FG2 }}>
            {fmtNumber(simulation.allowed)} of {fmtNumber(simulation.total_violations)} observed subresources would be allowed
          </Typography>
        </Stack>
        {/* Gauge bar */}
        <Box sx={{ mt: 1.5, height: 8, borderRadius: "4px", background: NEUTRAL_BG, overflow: "hidden" }}>
          <Box sx={{ width: `${Math.max(2, pct)}%`, height: "100%", background: barBg, transition: "width 250ms" }} />
        </Box>
        {simulation.blocked > 0 && (
          <Typography sx={{ fontSize: 12, color: FG3, mt: 1 }}>
            {fmtNumber(simulation.blocked)} would still be blocked — review the top offenders below and either add the missing origin, or accept the block if it's real noise.
          </Typography>
        )}
      </Box>

      {/* Per-directive coverage bars */}
      {simulation.directive_coverage?.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {simulation.directive_coverage.slice(0, 6).map((d) => {
            const dpct = Number(d.coverage_pct || 0);
            const dcolor = dpct >= 95 ? SUCCESS_FG : dpct >= 80 ? AMBER_FG : DANGER_FG;
            return (
              <Stack key={d.directive} direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 11.5, color: FG2, fontFamily: "ui-monospace, SFMono-Regular, monospace", minWidth: 130 }}>
                  {d.directive}
                </Typography>
                <Box sx={{ flex: 1, height: 5, borderRadius: "3px", background: NEUTRAL_BG, overflow: "hidden" }}>
                  <Box sx={{ width: `${Math.max(2, dpct)}%`, height: "100%", background: dcolor }} />
                </Box>
                <Typography sx={{ fontSize: 11, color: FG3, minWidth: 60, textAlign: "right" }}>
                  {dpct}% ({fmtNumber(d.allowed + d.blocked)})
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      )}

      {/* Top would-still-block */}
      {simulation.top_would_still_block?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75 }}>
            Top would-still-block
          </Typography>
          <Stack spacing={0.5}>
            {simulation.top_would_still_block.slice(0, 6).map((b, i) => (
              <Box
                key={i}
                sx={{
                  padding: "6px 10px",
                  background: NEUTRAL_BG,
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  overflow: "hidden",
                }}
              >
                <Chip
                  label={b.directive}
                  size="small"
                  sx={{ fontSize: 10.5, height: 18, background: "#fff", color: FG2, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
                />
                <Typography sx={{ fontSize: 11.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {b.blocked_uri || "(empty)"}
                </Typography>
                <Chip
                  label={`${fmtNumber(b.n)}×`}
                  size="small"
                  sx={{ fontSize: 10.5, height: 18, background: "#fff", color: FG3 }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// Phase 2.2b — review dialog for the auto-generated candidate policy.
// The parent (DiscoveryActiveBanner or top-level page) opens it with
// `open`, and the dialog owns the fetch + regenerate lifecycle so
// the operator can toggle `include_unsafe_inline` / `include_unsafe_eval`
// and re-fetch without any parent-side state coordination.
//
// The dialog does NOT auto-apply — it renders the candidate, the
// per-directive breakdown, the dropped-noise counts, and hands the
// operator two terminal actions:
//   * Copy: place the candidate on the clipboard so they can paste
//     it into their own review tool.
//   * Apply as Report only: save the policy_text, flip mode to
//     `report_only`, close the dialog. This is the "one-click
//     promotion" outcome Phase 2 has been building towards.
// ─────────────────────────────────────────────────────────────
// PolicyBuilderDialog — guided CSP authoring for operators who
// don't want to memorise the spec.
//
// Three sections in a single scrollable modal:
//   1. Framework preset — pick your site's stack (React SPA, Next.js,
//      WordPress, plain HTML, etc). Sets the base policy skeleton.
//   2. Third-party integrations — checkbox list of common vendors
//      (GA, GTM, Stripe, Hotjar, …). Each toggle adds the vendor's
//      origins to the right directives.
//   3. Advanced — Trusted Types, upgrade-insecure-requests,
//      block-mixed-content, unsafe- opt-ins (with warnings).
//
// On Apply we build a directive map, collapse duplicates, and set
// draft.policy_text — the operator can still hand-edit the result
// before hitting Save.
// ─────────────────────────────────────────────────────────────

// Framework skeletons. Each maps to a directives object that
// downstream code composes with the integrations. `null` values
// signify "don't emit this directive" so absence and empty-string
// stay distinguishable.
const BUILDER_FRAMEWORKS = [
  {
    key: "vanilla",
    label: "Vanilla / static site",
    desc: "Server-rendered HTML with no build step",
    dirs: {
      "default-src": "'self'",
      "script-src": "'self'",
      "style-src": "'self'",
      "img-src": "'self' data:",
      "font-src": "'self'",
      "connect-src": "'self'",
      "frame-ancestors": "'none'",
      "base-uri": "'self'",
      "form-action": "'self'",
      "object-src": "'none'",
    },
  },
  {
    key: "react-spa",
    label: "React SPA (CRA / Vite)",
    desc: "Client-rendered single-page app with a build step",
    dirs: {
      "default-src": "'self'",
      "script-src": "'self'",
      // Most React SPAs need inline styles for MUI/emotion — we set
      // 'unsafe-inline' by default here but Advanced can override.
      "style-src": "'self' 'unsafe-inline'",
      "img-src": "'self' data: https:",
      "font-src": "'self' data:",
      "connect-src": "'self'",
      "frame-ancestors": "'none'",
      "base-uri": "'self'",
      "form-action": "'self'",
      "object-src": "'none'",
    },
  },
  {
    key: "nextjs",
    label: "Next.js",
    desc: "React with SSR + client-side hydration",
    dirs: {
      "default-src": "'self'",
      "script-src": "'self' 'unsafe-inline'",
      "style-src": "'self' 'unsafe-inline'",
      "img-src": "'self' data: https:",
      "font-src": "'self' data:",
      "connect-src": "'self'",
      "frame-ancestors": "'none'",
      "base-uri": "'self'",
      "form-action": "'self'",
      "object-src": "'none'",
    },
  },
  {
    key: "wordpress",
    label: "WordPress",
    desc: "PHP-rendered site with plugin ecosystem",
    dirs: {
      "default-src": "'self'",
      "script-src": "'self' 'unsafe-inline' 'unsafe-eval'",
      "style-src": "'self' 'unsafe-inline'",
      "img-src": "'self' data: https:",
      "font-src": "'self' data: https:",
      "connect-src": "'self'",
      "frame-ancestors": "'self'",
      "base-uri": "'self'",
      "form-action": "'self'",
      "object-src": "'none'",
    },
  },
  {
    key: "checkout",
    label: "Payment checkout page",
    desc: "Tight policy — PCI DSS 4.0.1 §6.4.3 profile",
    dirs: {
      "default-src": "'none'",
      "script-src": "'self'",
      "style-src": "'self'",
      "img-src": "'self' data:",
      "font-src": "'self'",
      "connect-src": "'self'",
      "frame-src": "'self'",
      "frame-ancestors": "'none'",
      "base-uri": "'self'",
      "form-action": "'self'",
      "object-src": "'none'",
    },
  },
  {
    key: "blank",
    label: "Start blank",
    desc: "Empty skeleton — I want to author every directive",
    dirs: {
      "default-src": "'self'",
    },
  },
];

// Third-party integration cards. Each entry maps to the directives
// that need to accept the vendor's origins. `dirs` is a partial
// directives dict merged into the framework base.
const BUILDER_INTEGRATIONS = [
  {
    key: "ga4",
    label: "Google Analytics 4",
    desc: "gtag.js + collector endpoints",
    dirs: {
      "script-src": "https://www.googletagmanager.com https://www.google-analytics.com",
      "img-src": "https://www.google-analytics.com https://www.googletagmanager.com",
      "connect-src": "https://www.google-analytics.com https://www.googletagmanager.com https://analytics.google.com",
    },
  },
  {
    key: "gtm",
    label: "Google Tag Manager",
    desc: "container.js loader",
    dirs: {
      "script-src": "https://www.googletagmanager.com 'unsafe-inline'",
      "img-src": "https://www.googletagmanager.com",
      "connect-src": "https://www.googletagmanager.com",
    },
  },
  {
    key: "meta-pixel",
    label: "Meta / Facebook Pixel",
    desc: "fbevents.js",
    dirs: {
      "script-src": "https://connect.facebook.net",
      "img-src": "https://www.facebook.com https://connect.facebook.net",
      "connect-src": "https://www.facebook.com https://connect.facebook.net",
    },
  },
  {
    key: "stripe",
    label: "Stripe.js checkout",
    desc: "js.stripe.com + m.stripe.network",
    dirs: {
      "script-src": "https://js.stripe.com",
      "frame-src": "https://js.stripe.com https://hooks.stripe.com",
      "connect-src": "https://api.stripe.com",
      "img-src": "https://q.stripe.com",
    },
  },
  {
    key: "adyen",
    label: "Adyen Drop-in",
    desc: "checkoutshopper-live",
    dirs: {
      "script-src": "https://checkoutshopper-live.adyen.com",
      "frame-src": "https://checkoutshopper-live.adyen.com",
      "connect-src": "https://checkoutshopper-live.adyen.com",
      "img-src": "https://checkoutshopper-live.adyen.com",
    },
  },
  {
    key: "braintree",
    label: "Braintree",
    desc: "js.braintreegateway.com",
    dirs: {
      "script-src": "https://js.braintreegateway.com https://assets.braintreegateway.com",
      "frame-src": "https://assets.braintreegateway.com",
      "connect-src": "https://api.braintreegateway.com https://api.sandbox.braintreegateway.com",
      "img-src": "https://assets.braintreegateway.com",
    },
  },
  {
    key: "klaviyo",
    label: "Klaviyo",
    desc: "klaviyo.com onsite forms",
    dirs: {
      "script-src": "https://static.klaviyo.com",
      "connect-src": "https://a.klaviyo.com https://static.klaviyo.com",
      "img-src": "https://static.klaviyo.com",
    },
  },
  {
    key: "hotjar",
    label: "Hotjar",
    desc: "hj-collect.hotjar.com session replay",
    dirs: {
      "script-src": "https://static.hotjar.com https://script.hotjar.com",
      "connect-src": "https://*.hotjar.com wss://*.hotjar.com",
      "img-src": "https://*.hotjar.com",
      "font-src": "https://*.hotjar.com",
    },
  },
  {
    key: "fullstory",
    label: "FullStory",
    desc: "fullstory.com session recorder",
    dirs: {
      "script-src": "https://edge.fullstory.com https://fs.fullstory.com",
      "connect-src": "https://rs.fullstory.com https://*.fullstory.com",
      "img-src": "https://*.fullstory.com",
    },
  },
  {
    key: "turnstile",
    label: "Cloudflare Turnstile",
    desc: "challenges.cloudflare.com",
    dirs: {
      "script-src": "https://challenges.cloudflare.com",
      "frame-src": "https://challenges.cloudflare.com",
    },
  },
  {
    key: "recaptcha",
    label: "Google reCAPTCHA",
    desc: "www.google.com/recaptcha",
    dirs: {
      "script-src": "https://www.google.com https://www.gstatic.com",
      "frame-src": "https://www.google.com",
    },
  },
  {
    key: "sentry",
    label: "Sentry",
    desc: "browser.sentry-cdn.com error tracking",
    dirs: {
      "script-src": "https://browser.sentry-cdn.com",
      "connect-src": "https://*.ingest.sentry.io",
    },
  },
  {
    key: "datadog-rum",
    label: "Datadog RUM",
    desc: "browser-agent.js real-user monitoring",
    dirs: {
      "script-src": "https://www.datadoghq-browser-agent.com",
      "connect-src": "https://browser-intake-datadoghq.com https://*.browser-intake-datadoghq.com",
    },
  },
  {
    key: "intercom",
    label: "Intercom",
    desc: "widget.intercom.io chat widget",
    dirs: {
      "script-src": "https://widget.intercom.io https://js.intercomcdn.com",
      "connect-src": "https://api-iam.intercom.io https://nexus-websocket-a.intercom.io wss://nexus-websocket-a.intercom.io",
      "img-src": "https://js.intercomcdn.com https://static.intercomassets.com",
      "font-src": "https://js.intercomcdn.com",
    },
  },
];

// Advanced toggles for hardening + risk-carrying opt-ins.
const BUILDER_ADVANCED = [
  {
    key: "upgrade-insecure",
    label: "upgrade-insecure-requests",
    desc: "Rewrite http:// subresources to https:// before loading",
    directive: "upgrade-insecure-requests",
    value: "",  // no value, just presence
    warning: false,
  },
  {
    key: "block-mixed",
    label: "block-all-mixed-content",
    desc: "Reject any http:// subresource on an https:// page",
    directive: "block-all-mixed-content",
    value: "",
    warning: false,
  },
  {
    key: "trusted-types",
    label: "Trusted Types (require-trusted-types-for 'script')",
    desc: "Reject string→DOM sinks unless routed through a Trusted Types policy — mitigates DOM XSS",
    directive: "require-trusted-types-for",
    value: "'script'",
    warning: false,
  },
  {
    key: "unsafe-inline",
    label: "'unsafe-inline' in script-src",
    desc: "Weakens the policy — most modern frameworks don't need this. Only enable if legacy inline scripts break.",
    directive: "script-src",
    value: "'unsafe-inline'",
    warning: true,
  },
  {
    key: "unsafe-eval",
    label: "'unsafe-eval' in script-src",
    desc: "Weakens the policy — required only by a handful of build tools. Prefer nonces + hashes.",
    directive: "script-src",
    value: "'unsafe-eval'",
    warning: true,
  },
];

// Merge a partial `patch` into the base directives dict. Values are
// space-separated origin lists per CSP grammar; we split, dedupe, and
// re-join to keep the output canonical + terse.
function mergeDirectives(base, patch) {
  const out = { ...base };
  for (const [d, add] of Object.entries(patch || {})) {
    const existing = (out[d] || "").split(/\s+/).filter(Boolean);
    const incoming = String(add).split(/\s+/).filter(Boolean);
    const merged = [];
    for (const tok of existing.concat(incoming)) {
      if (!merged.includes(tok)) merged.push(tok);
    }
    out[d] = merged.join(" ");
  }
  return out;
}

// Compose the final policy_text from a directives dict. Sorts
// directives into a stable canonical order so operators diffing
// against a prior policy don't chase noise.
function composePolicyText(dirs) {
  const ORDER = [
    "default-src", "script-src", "script-src-elem", "script-src-attr",
    "style-src", "style-src-elem", "style-src-attr",
    "img-src", "font-src", "connect-src", "media-src",
    "frame-src", "child-src", "worker-src", "manifest-src", "object-src",
    "frame-ancestors", "base-uri", "form-action",
    "require-trusted-types-for", "trusted-types",
    "upgrade-insecure-requests", "block-all-mixed-content",
  ];
  const seen = new Set();
  const parts = [];
  for (const d of ORDER) {
    if (dirs[d] !== undefined && dirs[d] !== null) {
      seen.add(d);
      parts.push(dirs[d] === "" ? d : `${d} ${dirs[d]}`);
    }
  }
  for (const d of Object.keys(dirs)) {
    if (!seen.has(d)) {
      parts.push(dirs[d] === "" ? d : `${d} ${dirs[d]}`);
    }
  }
  return parts.join("; ");
}

function PolicyBuilderDialog({ open, onClose, onApply }) {
  const [preset, setPreset] = React.useState("react-spa");
  const [integrations, setIntegrations] = React.useState(() => new Set());
  const [advanced, setAdvanced] = React.useState(() => new Set(["upgrade-insecure"]));

  React.useEffect(() => {
    if (open) {
      // Reset when opened — don't preserve stale selections between
      // wizard invocations.
      setPreset("react-spa");
      setIntegrations(new Set());
      setAdvanced(new Set(["upgrade-insecure"]));
    }
  }, [open]);

  const preview = React.useMemo(() => {
    const frame = BUILDER_FRAMEWORKS.find((f) => f.key === preset) || BUILDER_FRAMEWORKS[0];
    let dirs = { ...frame.dirs };
    for (const key of integrations) {
      const spec = BUILDER_INTEGRATIONS.find((i) => i.key === key);
      if (spec) dirs = mergeDirectives(dirs, spec.dirs);
    }
    for (const key of advanced) {
      const spec = BUILDER_ADVANCED.find((a) => a.key === key);
      if (!spec) continue;
      if (spec.value === "") {
        // Presence-only directive (upgrade-insecure-requests, block-all-mixed-content).
        dirs[spec.directive] = "";
      } else if (spec.directive === "require-trusted-types-for") {
        dirs[spec.directive] = spec.value;
        dirs["trusted-types"] = "default 'allow-duplicates'";
      } else {
        dirs = mergeDirectives(dirs, { [spec.directive]: spec.value });
      }
    }
    return { dirs, text: composePolicyText(dirs) };
  }, [preset, integrations, advanced]);

  const toggle = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
        Guided policy builder
      </DialogTitle>
      <DialogContent dividers sx={{ background: "#FBFBFD" }}>
        <Stack spacing={3}>
          {/* Section 1: Framework preset */}
          <Box>
            <Typography sx={{ fontSize: 11, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, mb: 1.25 }}>
              1 · Site framework
            </Typography>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
              gap: "10px",
            }}>
              {BUILDER_FRAMEWORKS.map((f) => {
                const active = preset === f.key;
                return (
                  <Box
                    key={f.key}
                    onClick={() => setPreset(f.key)}
                    sx={{
                      cursor: "pointer",
                      border: `1px solid ${active ? INDIGO : BORDER}`,
                      background: active ? "rgba(99,113,247,0.06)" : "#FFF",
                      borderRadius: "10px",
                      padding: "12px 14px",
                      transition: "background 0.15s, border-color 0.15s",
                      "&:hover": { borderColor: INDIGO },
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: active ? INDIGO_INK : FG1 }}>
                      {f.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.25 }}>
                      {f.desc}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider />

          {/* Section 2: Third-party integrations */}
          <Box>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.25 }}>
              <Typography sx={{ fontSize: 11, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>
                2 · Third-party integrations
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: FG3 }}>
                Tick the vendors your site loads — we'll add their origins to the right directives.
              </Typography>
            </Stack>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: "8px",
            }}>
              {BUILDER_INTEGRATIONS.map((it) => {
                const active = integrations.has(it.key);
                return (
                  <Box
                    key={it.key}
                    onClick={() => toggle(setIntegrations)(it.key)}
                    sx={{
                      cursor: "pointer",
                      border: `1px solid ${active ? INDIGO : BORDER}`,
                      background: active ? "rgba(99,113,247,0.06)" : "#FFF",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      transition: "background 0.15s, border-color 0.15s",
                      "&:hover": { borderColor: INDIGO },
                    }}
                  >
                    <Checkbox
                      checked={active}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(setIntegrations)(it.key)}
                      sx={{ p: 0.25, "&.Mui-checked": { color: INDIGO } }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: active ? INDIGO_INK : FG1 }}>
                        {it.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: FG3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {it.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider />

          {/* Section 3: Advanced */}
          <Box>
            <Typography sx={{ fontSize: 11, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, mb: 1.25 }}>
              3 · Advanced (hardening + risk opt-ins)
            </Typography>
            <Stack spacing={0.5}>
              {BUILDER_ADVANCED.map((a) => {
                const active = advanced.has(a.key);
                return (
                  <Box
                    key={a.key}
                    onClick={() => toggle(setAdvanced)(a.key)}
                    sx={{
                      cursor: "pointer",
                      border: `1px solid ${active ? (a.warning ? "#F7C5CC" : INDIGO) : BORDER}`,
                      background: active ? (a.warning ? "rgba(251,55,72,0.05)" : "rgba(99,113,247,0.06)") : "#FFF",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                    }}
                  >
                    <Checkbox
                      checked={active}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(setAdvanced)(a.key)}
                      sx={{
                        p: 0.25,
                        "&.Mui-checked": { color: a.warning ? "#A11228" : INDIGO },
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: active ? (a.warning ? "#A11228" : INDIGO_INK) : FG1 }}>
                          {a.label}
                        </Typography>
                        {a.warning && (
                          <Chip
                            label="Weakens policy"
                            size="small"
                            sx={{ height: 18, fontSize: 10.5, fontWeight: 600, background: "#FEEBEE", color: "#A11228", border: "1px solid #F7C5CC" }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.25 }}>
                        {a.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Divider />

          {/* Preview */}
          <Box>
            <Typography sx={{ fontSize: 11, color: FG3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700, mb: 1.25 }}>
              Preview · {Object.keys(preview.dirs).length} directives
            </Typography>
            <Box sx={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 12.5,
              lineHeight: 1.6,
              color: FG1,
              background: "#FFF",
              border: `1px solid ${BORDER}`,
              borderRadius: "8px",
              padding: "12px 14px",
              wordBreak: "break-all",
              maxHeight: 220,
              overflow: "auto",
            }}>
              {preview.text || "(empty policy)"}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: "14px 22px", background: "#FAFAFA" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: FG2 }}>Cancel</Button>
        <PrimaryButton
          onClick={() => {
            onApply(preview.text);
            onClose();
          }}
        >
          Apply policy
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

function GeneratePolicyDialog({
  open,
  siteUid,
  generateCandidatePolicy,
  simulatePolicy,
  updateCspConfig,
  onClose,
  onApplied,
  showError,
  showSuccess,
}) {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [includeUnsafeInline, setIncludeUnsafeInline] = useState(false);
  const [includeUnsafeEval, setIncludeUnsafeEval] = useState(false);
  // Phase 2.3 — simulation state. The candidate always drives the
  // simulation, so we don't need a separate policy_text input here.
  const [simulation, setSimulation] = useState(null);
  const [simulating, setSimulating] = useState(false);

  const runGenerate = useCallback(async () => {
    if (!siteUid) return;
    setLoading(true);
    // Invalidate the previous simulation as soon as we start
    // regenerating — coverage is only meaningful against the
    // current candidate.
    setSimulation(null);
    try {
      const r = await generateCandidatePolicy(siteUid, {
        include_unsafe_inline: includeUnsafeInline,
        include_unsafe_eval: includeUnsafeEval,
      });
      setCandidate(r);
    } catch (e) {
      showError(
        e?.response?.data?.message || e?.message || "Failed to generate candidate policy."
      );
    } finally {
      setLoading(false);
    }
  }, [siteUid, generateCandidatePolicy, includeUnsafeInline, includeUnsafeEval, showError]);

  const runSimulate = useCallback(
    async (policyText) => {
      if (!siteUid || !policyText) return;
      setSimulating(true);
      try {
        const r = await simulatePolicy(siteUid, {
          policy_text: policyText,
          windowHours: candidate?.window_hours || 168,
        });
        setSimulation(r);
      } catch (e) {
        showError(
          e?.response?.data?.message || e?.message || "Failed to simulate policy."
        );
        setSimulation(null);
      } finally {
        setSimulating(false);
      }
    },
    [siteUid, simulatePolicy, candidate, showError]
  );

  // Fetch on open + refetch whenever the operator toggles either flag.
  useEffect(() => {
    if (open) runGenerate();
  }, [open, runGenerate]);

  // Auto-run simulation as soon as we have a candidate with at least
  // one violation to score against. Skipping the auto-run when the
  // candidate is empty avoids a useless "0/0 coverage" call.
  useEffect(() => {
    if (
      open &&
      !loading &&
      candidate &&
      candidate.policy_text &&
      candidate.total_violations > 0
    ) {
      runSimulate(candidate.policy_text);
    }
  }, [open, loading, candidate, runSimulate]);

  const handleApply = useCallback(async () => {
    if (!candidate?.policy_text || !siteUid) return;
    setApplying(true);
    const ok = await updateCspConfig(siteUid, {
      policy_text: candidate.policy_text,
      mode: "report_only",
    });
    setApplying(false);
    if (ok) {
      showSuccess("Policy applied. Site is now in Report only mode.");
      onApplied?.();
      onClose();
    }
  }, [candidate, siteUid, updateCspConfig, showSuccess, onApplied, onClose]);

  const handleCopy = useCallback(() => {
    if (!candidate?.policy_text) return;
    try {
      navigator.clipboard.writeText(candidate.policy_text);
      showSuccess("Candidate policy copied to clipboard.");
    } catch (e) {
      showError("Copy failed — select the text and press ⌘C.");
    }
  }, [candidate, showSuccess, showError]);

  const stats = candidate
    ? [
        { label: "Total violations", value: fmtNumber(candidate.total_violations) },
        { label: "Sources extracted", value: fmtNumber(candidate.sources_extracted) },
        { label: "Window (hours)", value: fmtNumber(candidate.window_hours) },
      ]
    : [];

  const dropped = candidate?.dropped || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        sx={{
          padding: "18px 24px",
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            background: INDIGO_BG,
            color: INDIGO,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": { width: 20, height: 20 },
          }}
        >
          <AutoAwesomeOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: FG1 }}>
            Candidate policy from discovery
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: FG2, mt: 0.25 }}>
            Layerd clustered the violations captured during discovery into a starting policy you can promote to Report only.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: "20px 24px" }}>
        {loading && (
          <Stack alignItems="center" sx={{ padding: "48px 0" }}>
            <CircularProgress size={28} sx={{ color: INDIGO }} />
            <Typography sx={{ fontSize: 12.5, color: FG3, mt: 1.5 }}>
              Clustering observed origins…
            </Typography>
          </Stack>
        )}

        {!loading && candidate && candidate.total_violations === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No violations were captured in the discovery window yet. Let discovery run for a full business cycle before generating — most sites benefit from at least a full weekday's traffic.
          </Alert>
        )}

        {!loading && candidate && (
          <Stack spacing={2.25}>
            {/* Headline stats */}
            <Stack direction="row" spacing={1.5}>
              {stats.map((s) => (
                <Box
                  key={s.label}
                  sx={{
                    flex: 1,
                    padding: "12px 14px",
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    background: NEUTRAL_BG,
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: FG1, lineHeight: 1.2, mt: 0.5 }}>
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Policy text */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Candidate policy
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={handleCopy}>
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Regenerate">
                    <IconButton size="small" onClick={runGenerate}>
                      <RefreshOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <TextField
                multiline
                minRows={4}
                maxRows={14}
                fullWidth
                value={candidate.policy_text || ""}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 },
                }}
              />
            </Box>

            {/* Opt-in toggles */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                Optional relaxations
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <GreenSwitch
                      checked={includeUnsafeInline}
                      onChange={(e) => setIncludeUnsafeInline(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1 }}>
                        Include <code>'unsafe-inline'</code>
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: FG3 }}>
                        Fold <code>inline</code> violations ({fmtNumber(dropped.unsafe_inline || 0)}) into the policy. Only enable when the site relies on inline scripts / styles you can't refactor.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <GreenSwitch
                      checked={includeUnsafeEval}
                      onChange={(e) => setIncludeUnsafeEval(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1 }}>
                        Include <code>'unsafe-eval'</code>
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: FG3 }}>
                        Fold <code>eval</code> / WebAssembly violations ({fmtNumber(dropped.unsafe_eval || 0)}). Same caveat — most modern sites can avoid this.
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Box>

            {/* Phase 2.3 coverage preview — auto-runs once we have a
                candidate. Renders a headline % + a full-width gauge
                bar coloured by outcome, plus the top would-still-block
                chips so the operator can spot missing origins. */}
            {candidate.total_violations > 0 && (
              <CoveragePreview simulation={simulation} simulating={simulating} />
            )}

            {/* Per-directive breakdown */}
            {candidate.directives?.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                  Per-directive breakdown
                </Typography>
                <Stack spacing={1}>
                  {candidate.directives.map((d) => (
                    <Box
                      key={d.name}
                      sx={{
                        padding: "10px 14px",
                        border: `1px solid ${BORDER}`,
                        borderRadius: "10px",
                        background: "#fff",
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                          {d.name}
                        </Typography>
                        <Chip
                          label={`${fmtNumber(d.count)} violations`}
                          size="small"
                          sx={{ fontSize: 11, height: 20, background: NEUTRAL_BG, color: FG2 }}
                        />
                        <Chip
                          label={`${fmtNumber(d.sources?.length || 0)} sources`}
                          size="small"
                          sx={{ fontSize: 11, height: 20, background: INDIGO_BG, color: INDIGO }}
                        />
                      </Stack>
                      <Typography sx={{ fontSize: 11.5, color: FG2, mt: 0.5, fontFamily: "ui-monospace, SFMono-Regular, monospace", wordBreak: "break-all" }}>
                        {d.sources?.join(" ") || "'none'"}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Dropped-noise summary */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75 }}>
                Noise filtered out
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${fmtNumber(dropped.extensions || 0)} browser-extension`} sx={{ fontSize: 11.5, height: 22, background: NEUTRAL_BG, color: FG2 }} />
                <Chip size="small" label={`${fmtNumber(dropped.same_origin || 0)} same-origin`} sx={{ fontSize: 11.5, height: 22, background: NEUTRAL_BG, color: FG2 }} />
                <Chip size="small" label={`${fmtNumber(dropped.empty || 0)} unparseable`} sx={{ fontSize: 11.5, height: 22, background: NEUTRAL_BG, color: FG2 }} />
                <Chip size="small" label={`${fmtNumber(dropped.unsafe_inline || 0)} inline`} sx={{ fontSize: 11.5, height: 22, background: NEUTRAL_BG, color: FG2 }} />
                <Chip size="small" label={`${fmtNumber(dropped.unsafe_eval || 0)} eval`} sx={{ fontSize: 11.5, height: 22, background: NEUTRAL_BG, color: FG2 }} />
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "14px 24px", borderTop: `1px solid ${BORDER}` }}>
        <Button
          onClick={onClose}
          sx={{ color: FG2, textTransform: "none" }}
        >
          Cancel
        </Button>
        <PrimaryButton
          onClick={handleApply}
          disabled={!candidate?.policy_text || applying || loading || candidate?.total_violations === 0}
          startIcon={<ShieldOutlinedIcon />}
        >
          {applying ? "Applying…" : "Apply as Report only"}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

// section: undefined → render all (backward compat).
//   "status"     — Card 1 only
//   "policy"     — Card 1 + Card 2 + Discovery banner (they share the same
//                  editable draft so cannot cleanly be separated)
//   "violations" — Card 3 only (ViolationDrawer stays mounted)
//   "routes"     — Card 4 only (add/edit navigates to /v2/csp/override)
//   "intel"      — Card 5 only
function CspConfig({ section } = {}) {
  const showAll = !section;
  const showStatus  = showAll || section === "status" || section === "policy";
  const showPolicy  = showAll || section === "policy";
  const showViol    = showAll || section === "violations";
  const showRoutes  = showAll || section === "routes";
  const showIntel   = showAll || section === "intel";
  const { effectiveSiteUid } = useGlobalSite();
  const { showSuccess, showError, showInfo } = useNotification();
  const navigate = useNavigate();
  const {
    cspConfig,
    cspViolations,
    getCspConfig,
    updateCspConfig,
    getCspViolations,
    generateCandidatePolicy,
    simulatePolicy,
    getViolationIntel,
    listRouteOverrides,
    deleteRouteOverride,
    getEdgeHealth,
    errMsg,
    setErr,
  } = useCspConfig();
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [builderDialogOpen, setBuilderDialogOpen] = useState(false);
  // Phase 5 — Violation intelligence panel state.
  const [intelData, setIntelData] = useState(null);
  const [intelWindowHours, setIntelWindowHours] = useState(24);
  // Phase 6.3 — route overrides panel state.
  const [routeOverrides, setRouteOverrides] = useState([]);
  const [routeOverridesLoading, setRouteOverridesLoading] = useState(false);
  const [routeOverrideActionMenu, setRouteOverrideActionMenu] = useState({ anchorEl: null, rowId: null });
  const [pendingRouteOverrideDelete, setPendingRouteOverrideDelete] = useState(null);
  const [routeOverrideDeleting, setRouteOverrideDeleting] = useState(false);
  const [intelLoading, setIntelLoading] = useState(false);

  const site_uid = effectiveSiteUid || null;

  // Editable draft — mirrors the server row shape so cancel is a
  // one-liner reset.
  const [draft, setDraft] = useState({
    mode: "off",
    policy_text: "",
    enabled: true,
    report_rate_cap_per_min: 10000,
    sri_mode: "off",
    trusted_types_enabled: false,
    nonce_mode: "off",
  });
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  // Gap-closure sprint — edge health snapshot. Answers "is the edge
  // actually collecting anything right now" so operators catch dead
  // configurations (headers not injected, hot-reload didn't fire,
  // report token mismatch) before they cause a QSA finding.
  const [edgeHealth, setEdgeHealth] = useState(null);

  useEffect(() => {
    if (site_uid) {
      getCspConfig(site_uid);
      getCspViolations(site_uid, { windowHours: 24, recentLimit: 50 });
    }
  }, [site_uid, getCspConfig, getCspViolations]);

  // Edge health snapshot — poll every 60s while the page is mounted.
  // Fail-open: if the endpoint is down, we just leave the tile in
  // its initial "loading" state rather than showing a red banner.
  useEffect(() => {
    if (!site_uid || !getEdgeHealth || !showStatus) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await getEdgeHealth(site_uid);
        if (!cancelled) setEdgeHealth(r);
      } catch (e) { /* swallow */ }
    };
    tick();
    const t = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [site_uid, getEdgeHealth, showStatus]);

  // Phase 5 — pull intelligence aggregates on load + whenever the
  // window changes. Silent on failure (panel renders zero state).
  const loadIntel = useCallback(async () => {
    if (!site_uid) return;
    setIntelLoading(true);
    try {
      const r = await getViolationIntel(site_uid, { windowHours: intelWindowHours });
      setIntelData(r);
    } catch (e) {
      setIntelData(null);
    } finally {
      setIntelLoading(false);
    }
  }, [site_uid, intelWindowHours, getViolationIntel]);
  useEffect(() => { loadIntel(); }, [loadIntel]);

  // Phase 6.3 — load route overrides on mount. Create/edit happens on
  // the dedicated /v2/csp/override full-page form.
  const loadRouteOverrides = useCallback(async () => {
    if (!site_uid) return;
    setRouteOverridesLoading(true);
    try {
      const r = await listRouteOverrides(site_uid);
      setRouteOverrides(r?.overrides || []);
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || "Failed to load route overrides.");
      setRouteOverrides([]);
    } finally {
      setRouteOverridesLoading(false);
    }
  }, [site_uid, listRouteOverrides, showError]);
  useEffect(() => { loadRouteOverrides(); }, [loadRouteOverrides]);

  const requestRouteOverrideDelete = useCallback((row) => {
    if (!row) return;
    setPendingRouteOverrideDelete(row);
  }, []);

  const executeRouteOverrideDelete = useCallback(async () => {
    const row = pendingRouteOverrideDelete;
    if (!site_uid || !row?.id) return;
    setRouteOverrideDeleting(true);
    try {
      await deleteRouteOverride(site_uid, row.id);
      showSuccess(`Route override for ${row.path_pattern} deleted.`);
      setPendingRouteOverrideDelete(null);
      await loadRouteOverrides();
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || "Delete failed.");
    } finally {
      setRouteOverrideDeleting(false);
    }
  }, [site_uid, pendingRouteOverrideDelete, deleteRouteOverride, loadRouteOverrides, showSuccess, showError]);

  useEffect(() => {
    if (cspConfig) {
      setDraft({
        mode: cspConfig.mode || "off",
        policy_text: cspConfig.policy_text || "",
        enabled: !!cspConfig.enabled,
        report_rate_cap_per_min: cspConfig.report_rate_cap_per_min || 10000,
        sri_mode: cspConfig.sri_mode || "off",
        trusted_types_enabled: !!cspConfig.trusted_types_enabled,
        nonce_mode: cspConfig.nonce_mode || "off",
      });
    }
  }, [cspConfig]);

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
      setErr(null);
    }
  }, [errMsg, showError, setErr]);

  const dirty = useMemo(() => {
    if (!cspConfig) return false;
    return (
      draft.mode !== (cspConfig.mode || "off") ||
      draft.policy_text !== (cspConfig.policy_text || "") ||
      draft.enabled !== !!cspConfig.enabled ||
      Number(draft.report_rate_cap_per_min) !==
        Number(cspConfig.report_rate_cap_per_min || 10000) ||
      draft.sri_mode !== (cspConfig.sri_mode || "off") ||
      draft.trusted_types_enabled !== !!cspConfig.trusted_types_enabled ||
      draft.nonce_mode !== (cspConfig.nonce_mode || "off")
    );
  }, [draft, cspConfig]);

  const modifiedFields = useMemo(() => {
    if (!cspConfig) return [];
    const out = [];
    if (draft.mode !== (cspConfig.mode || "off")) out.push("Rollout mode");
    if (draft.enabled !== !!cspConfig.enabled) out.push("Master kill-switch");
    if (draft.sri_mode !== (cspConfig.sri_mode || "off")) out.push("SRI enforcement");
    if (draft.trusted_types_enabled !== !!cspConfig.trusted_types_enabled) out.push("Trusted Types");
    if (draft.nonce_mode !== (cspConfig.nonce_mode || "off")) out.push("Inline-tag nonce");
    if (draft.policy_text !== (cspConfig.policy_text || "")) out.push("Policy text");
    if (
      Number(draft.report_rate_cap_per_min) !==
      Number(cspConfig.report_rate_cap_per_min || 10000)
    ) {
      out.push("Report rate cap");
    }
    return out;
  }, [draft, cspConfig]);

  const totals = cspViolations?.total_count ?? null;
  const topDirective = cspViolations?.by_directive?.[0] || null;
  const modeMeta = MODES.find((m) => m.key === (cspConfig?.mode || "off")) || MODES[0];

  const handleSave = useCallback(async () => {
    if (!site_uid) return;
    setSaving(true);
    const ok = await updateCspConfig(site_uid, {
      mode: draft.mode,
      policy_text: draft.policy_text,
      enabled: draft.enabled,
      report_rate_cap_per_min: Math.max(60, Number(draft.report_rate_cap_per_min) || 10000),
      sri_mode: draft.sri_mode,
      trusted_types_enabled: draft.trusted_types_enabled,
      nonce_mode: draft.nonce_mode,
    });
    setSaving(false);
    if (ok) {
      showSuccess("CSP configuration saved.");
      await getCspViolations(site_uid, { windowHours: 24, recentLimit: 50 });
    }
  }, [site_uid, draft, updateCspConfig, showSuccess, getCspViolations]);

  const handleDiscard = useCallback(() => {
    if (!cspConfig) return;
    setDraft({
      mode: cspConfig.mode || "off",
      policy_text: cspConfig.policy_text || "",
      enabled: !!cspConfig.enabled,
      report_rate_cap_per_min: cspConfig.report_rate_cap_per_min || 10000,
      sri_mode: cspConfig.sri_mode || "off",
      trusted_types_enabled: !!cspConfig.trusted_types_enabled,
      nonce_mode: cspConfig.nonce_mode || "off",
    });
  }, [cspConfig]);

  const handleRotateToken = useCallback(async () => {
    if (!site_uid) return;
    const ok = await updateCspConfig(site_uid, { rotate_report_token: true });
    if (ok) {
      showSuccess("Report token rotated. Previously-issued report URLs are now invalid.");
    }
  }, [site_uid, updateCspConfig, showSuccess]);

  const handleCopyToken = useCallback(() => {
    if (!cspConfig?.report_token) return;
    try {
      navigator.clipboard.writeText(cspConfig.report_token);
      showSuccess("Report token copied to clipboard.");
    } catch (e) {
      showError("Copy failed — select the field and press ⌘C.");
    }
  }, [cspConfig, showSuccess, showError]);

  // Section-scoped header copy. Each dedicated CSP page (Policy /
  // Violations / Routes / Intelligence / Override) mounts CspConfig
  // with `section` set; when unset we fall back to the roll-up copy
  // used by the legacy /services/csp/config path.
  const sectionMeta = React.useMemo(() => {
    switch (section) {
      case "policy":
        return {
          title: "Policy configuration",
          subtitle: "Pick a rollout mode, author the policy, and configure the report ingest. Report only produces telemetry without blocking; Enforce blocks matching violations.",
        };
      case "violations":
        return {
          title: "Recent violations",
          subtitle: "Live feed of browser-reported violations, grouped by directive and blocked URI. Counts aggregate identical reports over the selected window.",
        };
      case "routes":
        return {
          title: "Route policies",
          subtitle: "Per-path CSP overrides for this site. The longest matching prefix wins — segment-boundary aware — so /api matches /api/x but not /apix.",
        };
      case "intel":
        return {
          title: "Violation intelligence",
          subtitle: "Aggregate view of browser reports: what's blocked most, which pages generate them, and what looks like a real campaign vs. one-off noise.",
        };
      case "override":
        return {
          title: "New CSP override",
          subtitle: "Author a per-path override. On save you'll return to the Route policies list; existing overrides can be edited from there.",
        };
      default:
        return {
          title: "Content Security Policy",
          subtitle: "Instruct the browser what code your site is allowed to load. Start in Report only, review real violations, promote to Enforce when the noise clears.",
        };
    }
  }, [section]);

  return (
    <SiteScopeGate mode="single">
      {() => (
        <>
      <Helmet>
        <title>{sectionMeta.title}</title>
      </Helmet>
      <Stack spacing={3} sx={{ p: 4 }}>
        <V2PageHeader
          title={sectionMeta.title}
          description={sectionMeta.subtitle}
          sx={{ pb: 1.5 }}
          descriptionSx={{ maxWidth: 760, mt: 1 }}
        />

        {!cspConfig && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={28} sx={{ color: INDIGO }} />
          </Stack>
        )}

        {cspConfig && (
          <Stack spacing={2.5}>
          {/* ── Card 1: Protection status tiles (standalone) ─────── */}
          {showStatus && (
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <Box sx={{ flex: 1 }}>
                <StatusTile
                  Icon={modeMeta.Icon}
                  label="Current mode"
                  value={
                    <Chip
                      label={modeMeta.label}
                      size="small"
                      sx={{
                        height: 26,
                        fontSize: 13,
                        fontWeight: 700,
                        background: modeMeta.tone.bg,
                        color: modeMeta.tone.fg,
                        border: `1px solid ${modeMeta.tone.br}`,
                      }}
                    />
                  }
                  sub={cspConfig.enabled ? "Kill-switch on" : "Kill-switch off — header suppressed"}
                  accent={modeMeta.key !== "off"}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatusTile
                  Icon={ReportProblemOutlinedIcon}
                  label="Violations · last 24h"
                  value={fmtNumber(totals)}
                  sub={topDirective ? `Top: ${topDirective.directive} (${fmtNumber(topDirective.n)})` : "No violations yet"}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StatusTile
                  Icon={TimerOutlinedIcon}
                  label="Report rate cap"
                  value={`${fmtNumber(cspConfig.report_rate_cap_per_min)} /min`}
                  sub="Per-site ceiling on inbound reports"
                />
              </Box>
              {/* Edge health tile — collapses into the same 4-across
                  row on wide screens so operators see it side-by-side
                  with configuration state. */}
              <Box sx={{ flex: 1 }}>
                <StatusTile
                  Icon={InsightsOutlinedIcon}
                  label="Edge collection · last hour"
                  value={
                    edgeHealth == null
                      ? "…"
                      : edgeHealth.reports_last_hour === 0
                      ? "0"
                      : fmtNumber(edgeHealth.reports_last_hour)
                  }
                  sub={
                    edgeHealth == null
                      ? "Checking edge…"
                      : edgeHealth.reports_last_hour === 0
                      ? "No reports received — verify header injection"
                      : `${fmtNumber(edgeHealth.distinct_uris)} distinct URIs · last ${
                          edgeHealth.last_report_ms
                            ? new Date(edgeHealth.last_report_ms).toLocaleTimeString()
                            : "—"
                        }`
                  }
                  accent={edgeHealth?.reports_last_hour > 0}
                />
              </Box>
            </Stack>
          )}

          {/* ── Discovery banner ────────────────────────────────── */}
          {showPolicy && cspConfig.mode === "discovery" && (
            <DiscoveryActiveBanner
              startedAt={cspConfig.discovery_start_at}
              violationCount={totals}
              directiveCount={cspViolations?.by_directive?.length ?? 0}
              onEndDiscovery={async () => {
                if (!site_uid) return;
                // Ending discovery flips back to "off" so the operator
                // has to consciously pick their next mode after
                // reviewing the candidate policy. Phase 2.2 will add
                // a "Promote to report-only" flow that generates the
                // candidate and lets the operator apply it in one
                // click.
                const ok = await updateCspConfig(site_uid, { mode: "off" });
                if (ok) {
                  showSuccess("Discovery ended. Review the captured violations to seed your policy.");
                  await getCspViolations(site_uid, { windowHours: 24 * 30, recentLimit: 50 });
                }
              }}
              onGeneratePolicy={() => setGenerateDialogOpen(true)}
            />
          )}

          {/* ── Card 2: Policy configuration ─────────────────────── */}
          {showPolicy && (
          <SectionCard
            icon={<PolicyOutlinedIcon />}
            title="Policy configuration"
            desc="Pick a rollout mode, author or paste the policy, and configure the report ingest. Report only produces telemetry without blocking; Enforce blocks matching violations."
          >
            <Stack spacing={2.5}>
              {/* Mode picker */}
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Rollout mode
                </Typography>
                <ModePickerRow
                  current={draft.mode}
                  onPick={(k) => setDraft((d) => ({ ...d, mode: k }))}
                />
              </Box>

              {/* Kill switch */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1 }}>
                      Master kill-switch
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: FG2, mt: 0.25 }}>
                      Suppresses the CSP header on all responses regardless of mode. Toggle off to instantly stop injection during an incident.
                    </Typography>
                  </Box>
                  <GreenSwitch
                    checked={draft.enabled}
                    onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                  />
                </Stack>
              </Box>

              {/* Phase 4a/b — Subresource Integrity toggle. Independent
                  of the CSP mode picker. When on, the edge rewrites
                  <script src="…"> tags to include integrity="sha256-…"
                  for URLs in the authorized inventory that have a
                  recorded content hash. Browsers block execution of
                  any script whose fetched bytes don't match. */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box sx={{ pr: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <LockOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1 }}>
                        Subresource Integrity (SRI) enforcement
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: FG2, mt: 0.35 }}>
                      When on, the edge injects <code>integrity="sha256-…"</code> on <code>&lt;script src=…&gt;</code> tags whose URL is in the authorized inventory and has a recorded content hash. Browsers refuse to execute any script whose fetched bytes don't match — a direct client-side defence in addition to the PCI 11.6.1 drift alerts.
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.5 }}>
                      Only authorized scripts with a hash are eligible. Empty inventory = no-op. See the Script inventory page for coverage.
                    </Typography>
                  </Box>
                  <GreenSwitch
                    checked={draft.sri_mode === "enforce"}
                    onChange={(e) => setDraft((d) => ({ ...d, sri_mode: e.target.checked ? "enforce" : "off" }))}
                  />
                </Stack>
              </Box>

              {/* Phase 6.2 — Trusted Types toggle. Rides on top of
                  the CSP mode: when on, pingora appends the two TT
                  directives to the emitted policy. Rejects string-
                  to-DOM-sink assignments unless routed through a
                  Trusted Types policy. */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box sx={{ pr: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <ShieldOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1 }}>
                        Trusted Types (DOM XSS defence)
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: FG2, mt: 0.35 }}>
                      When on, the edge appends <code>require-trusted-types-for 'script'</code> and <code>trusted-types default 'allow-duplicates'</code> to the emitted CSP header. Browsers refuse to accept plain strings on DOM XSS sinks (<code>innerHTML</code>, <code>eval</code>, <code>script.src=</code>, etc) unless the string is routed through a Trusted Types policy.
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.5 }}>
                      Rides on top of the CSP mode above — Report only surfaces violations without blocking; Enforce blocks. Has no effect when mode is Off.
                    </Typography>
                  </Box>
                  <GreenSwitch
                    checked={draft.trusted_types_enabled}
                    onChange={(e) => setDraft((d) => ({ ...d, trusted_types_enabled: e.target.checked }))}
                  />
                </Stack>
              </Box>

              {/* Phase 6.4 — Nonce picker. Three-state select instead
                  of a switch because ScriptAndStyle is a third
                  meaningful state, not a "more" of Script. */}
              <Box>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ pr: 2, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <AutoAwesomeOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: FG1 }}>
                        Inline-tag nonce
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: FG2, mt: 0.35 }}>
                      When on, the edge mints a fresh per-response nonce, tags matching inline tags with <code>nonce="…"</code>, and appends <code>'nonce-&lt;b64&gt;'</code> to the CSP directives. Browsers accept ONLY the tagged inlines — meaning you can drop <code>'unsafe-inline'</code> from your policy.
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.5 }}>
                      Only rewrites existing <code>script-src</code> / <code>style-src</code> directives. If your policy relies on <code>default-src</code>, add <code>script-src</code> explicitly first.
                    </Typography>
                  </Box>
                  <Select
                    value={draft.nonce_mode}
                    onChange={(e) => setDraft((d) => ({ ...d, nonce_mode: e.target.value }))}
                    size="small"
                    sx={{ minWidth: 220, fontSize: 13 }}
                  >
                    <MenuItem value="off">Off — no nonce</MenuItem>
                    <MenuItem value="script">Script — inline &lt;script&gt; only</MenuItem>
                    <MenuItem value="script_and_style">Script + Style — both</MenuItem>
                  </Select>
                </Stack>
              </Box>

              <Divider />

              {/* Policy textarea */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Policy text
                  </Typography>
                  <Button
                    onClick={() => setBuilderDialogOpen(true)}
                    size="small"
                    variant="outlined"
                    disableElevation
                    startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                    sx={cspOutlinedButtonSx}
                  >
                    Guided builder
                  </Button>
                </Stack>
                <TextField
                  multiline
                  minRows={5}
                  maxRows={18}
                  fullWidth
                  value={draft.policy_text}
                  onChange={(e) => setDraft((d) => ({ ...d, policy_text: e.target.value }))}
                  placeholder="default-src 'self'; script-src 'self' https://cdn.example.com; style-src 'self' 'unsafe-inline'"
                  inputProps={{ maxLength: 64000, style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 } }}
                />
                {draft.mode === "discovery" ? (
                  <Typography sx={{ fontSize: 11.5, color: INDIGO, mt: 0.75, fontWeight: 600 }}>
                    Discovery mode is active — the policy above is currently ignored. Your text is saved for when you leave discovery, but the browser is being sent{" "}
                    <code>default-src 'none'; …</code> so we can learn your full inventory.
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.75 }}>
                    If the policy doesn't declare a <code>report-uri</code> or <code>report-to</code>, we automatically append a report destination pointing at your per-site token URL below. Maximum 64,000 characters.
                  </Typography>
                )}
              </Box>

              {/* Report token */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <KeyOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Report token
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    value={showToken ? (cspConfig.report_token || "") : "•".repeat(Math.min((cspConfig.report_token || "").length, 40))}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 13,
                        height: CSP_TOKEN_CONTROL_H,
                        boxSizing: "border-box",
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-root": { height: CSP_TOKEN_CONTROL_H, boxSizing: "border-box" },
                      "& .MuiInputBase-input": { py: 0, height: CSP_TOKEN_CONTROL_H, boxSizing: "border-box" },
                    }}
                  />
                  <Tooltip title={showToken ? "Hide token" : "Show token"}>
                    <IconButton size="small" onClick={() => setShowToken((v) => !v)} sx={cspIconButtonSx}>
                      {showToken ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy to clipboard">
                    <IconButton size="small" onClick={handleCopyToken} sx={cspIconButtonSx}>
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Rotate token">
                    <IconButton size="small" onClick={handleRotateToken} sx={cspIconButtonSx}>
                      <RefreshOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.75 }}>
                  Reports are received at <code>/_ld/csp-report/&lt;token&gt;</code> on your site. Rotating invalidates any previously-issued report URLs; browsers will start using the new one on their next page load.
                </Typography>
              </Box>

              {/* Rate cap */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <TimerOutlinedIcon sx={{ fontSize: 15, color: FG2 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: FG2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Report rate cap
                  </Typography>
                </Stack>
                <TextField
                  type="number"
                  size="small"
                  value={draft.report_rate_cap_per_min}
                  onChange={(e) => setDraft((d) => ({ ...d, report_rate_cap_per_min: e.target.value }))}
                  inputProps={{ min: 60, max: 10000000 }}
                  sx={{
                    width: 220,
                    "& .MuiInputBase-root": { height: CSP_TOKEN_CONTROL_H, boxSizing: "border-box" },
                    "& .MuiInputBase-input": { py: 0, height: CSP_TOKEN_CONTROL_H, boxSizing: "border-box" },
                  }}
                  InputProps={{
                    sx: { height: CSP_TOKEN_CONTROL_H, boxSizing: "border-box" },
                    endAdornment: <Typography sx={{ fontSize: 12, color: FG3, ml: 1 }}>reports / min</Typography>,
                  }}
                />
                <Typography sx={{ fontSize: 11.5, color: FG3, mt: 0.75 }}>
                  Excess reports are dropped and counted in the drop metric. A misconfigured policy on a busy page can send tens of thousands of reports per minute — the cap protects the ingest.
                </Typography>
              </Box>
            </Stack>
          </SectionCard>
          )}

          {/* ── Card 3: Recent violations ───────────────────────── */}
          {showViol && (
          <SectionCard
            icon={<ReportProblemOutlinedIcon />}
            title="Recent violations"
            desc="Live feed of browser-reported violations. Click a row for the full detail. Filter by directive from the top-N chips."
            right={
              <RefreshIconButton
                onClick={() => site_uid && getCspViolations(site_uid, { windowHours: 24, recentLimit: 50 })}
                size="compact"
              />
            }
            bodyPad="8px 4px 12px"
          >
            {/* Directive chips */}
            {cspViolations?.by_directive?.length ? (
              <Box sx={{ padding: "10px 16px 12px" }}>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {cspViolations.by_directive.slice(0, 8).map((d) => (
                    <Chip
                      key={d.directive}
                      label={`${d.directive} · ${fmtNumber(d.n)}`}
                      size="small"
                      onClick={() => site_uid && getCspViolations(site_uid, { windowHours: 24, recentLimit: 50, directive: d.directive })}
                      sx={{ fontSize: 11.5, height: 22, background: NEUTRAL_BG, color: FG2, cursor: "pointer" }}
                    />
                  ))}
                  <Chip
                    label="All"
                    size="small"
                    onClick={() => site_uid && getCspViolations(site_uid, { windowHours: 24, recentLimit: 50 })}
                    sx={{ fontSize: 11.5, height: 22, background: INDIGO_BG, color: INDIGO, cursor: "pointer" }}
                  />
                </Stack>
              </Box>
            ) : null}

            {/* Empty state */}
            {(!cspViolations?.recent || cspViolations.recent.length === 0) && (
              <Box sx={{ padding: "36px 20px", textAlign: "center" }}>
                <Typography sx={{ fontSize: 13.5, color: FG2, mb: 0.5 }}>
                  No violations reported in the last 24 hours.
                </Typography>
                <Typography sx={{ fontSize: 12, color: FG3 }}>
                  Flip a site to <b>Report only</b> to start collecting telemetry without any risk to your traffic.
                </Typography>
              </Box>
            )}

            {/* Table */}
            {cspViolations?.recent && cspViolations.recent.length > 0 && (
              <Table size="small" sx={{ mt: 0 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Time</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Directive</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Blocked URI</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Document</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Mode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cspViolations.recent.map((r, i) => {
                    const dir = r.effective_directive || r.violated_directive || "—";
                    return (
                      <TableRow
                        key={`${r.ts}-${i}`}
                        hover
                        onClick={() => setDrawerRow(r)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ fontSize: 12.5, color: FG1, whiteSpace: "nowrap" }}>
                          {fmtWhen(r.ts)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                          {dir}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {shortenUri(r.blocked_uri, 42)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: FG2, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {shortenUri(r.document_uri, 36)}
                        </TableCell>
                        <TableCell>
                          {r.mode ? (
                          <Chip
                            label={r.mode}
                            size="small"
                            sx={{
                              fontSize: 10.5,
                              height: 18,
                              background: r.mode === "enforce" ? SUCCESS_BG : AMBER_BG,
                              color: r.mode === "enforce" ? SUCCESS_FG : AMBER_FG,
                            }}
                          />
                          ) : (
                            <Typography sx={{ fontSize: 12.5, color: FG2 }}>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </SectionCard>
          )}

          {/* ── Card 4.5 — Phase 6.3: Route-level policies ──────── */}
          {showRoutes && (
          <SectionCard
            icon={<PolicyOutlinedIcon />}
            title="Route policies"
            desc="Per-path overrides for the site-wide policy above. The longest matching prefix is used on every request (segment-boundary aware — /api matches /api/x but not /apix). Only mode + policy text are per-route; SRI and Trusted Types stay site-wide."
            right={
              <Stack direction="row" spacing={1} alignItems="center">
                <RefreshIconButton
                  onClick={loadRouteOverrides}
                  disabled={routeOverridesLoading}
                  loading={routeOverridesLoading}
                  size="compact"
                />
                <PrimaryButton
                  size="small"
                  startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate("/v2/csp/override")}
                >
                  Add override
                </PrimaryButton>
              </Stack>
            }
          >
            {routeOverridesLoading && routeOverrides.length === 0 ? (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={22} sx={{ color: INDIGO }} />
              </Stack>
            ) : routeOverrides.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography sx={{ fontSize: 13, color: FG2 }}>
                  No route overrides for this site. All requests use the site-wide policy above.
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Path prefix</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Mode</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Policy</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Enabled</TableCell>
                    <TableCell sx={{ fontSize: 11, color: FG3, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Updated</TableCell>
                    <TableCell align="right" sx={{ width: 96 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routeOverrides.map((r) => {
                    const modeMeta = MODES.find((m) => m.key === r.mode) || MODES[0];
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontSize: 12.5, color: FG1, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontWeight: 600 }}>
                          {r.path_pattern}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={modeMeta.label}
                            size="small"
                            sx={{ fontSize: 11, height: 20, fontWeight: 600, background: modeMeta.tone.bg, color: modeMeta.tone.fg, border: `1px solid ${modeMeta.tone.br}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: FG2, fontFamily: "ui-monospace, SFMono-Regular, monospace", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Tooltip title={r.policy_text || "(empty — no header emitted for matched paths)"}>
                            <span>{r.policy_text ? shortenUri(r.policy_text, 50) : <em>(empty)</em>}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={r.enabled ? "On" : "Off"}
                            size="small"
                            sx={{
                              fontSize: 11, height: 20, fontWeight: 600,
                              background: r.enabled ? SUCCESS_BG : NEUTRAL_BG,
                              color: r.enabled ? SUCCESS_FG : FG3,
                              border: `1px solid ${r.enabled ? SUCCESS_BORDER : BORDER}`,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: FG3, whiteSpace: "nowrap" }}>
                          {fmtWhen(r.updated_at)}
                        </TableCell>
                        <TableCell align="right">
                          <Stack alignItems="flex-end" sx={{ width: "100%" }}>
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                setRouteOverrideActionMenu({ anchorEl: e.currentTarget, rowId: r.id });
                              }}
                              sx={{ cursor: "pointer", width: 24, height: 24 }}
                            >
                              <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                            </Box>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </SectionCard>
          )}

          <Menu
            anchorEl={routeOverrideActionMenu.anchorEl}
            open={Boolean(routeOverrideActionMenu.anchorEl)}
            onClose={() => setRouteOverrideActionMenu({ anchorEl: null, rowId: null })}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={{
              top: 13,
              left: -12,
              "& .MuiList-root": {
                backgroundColor: "white",
                borderRadius: "8px",
                border: `1px solid ${colors.neutral[200]}`,
                width: 200,
                boxShadow: "none",
                py: 0,
              },
              "& .MuiMenu-paper": { boxShadow: "none" },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                  const row = routeOverrides.find((item) => item.id === routeOverrideActionMenu.rowId);
                  setRouteOverrideActionMenu({ anchorEl: null, rowId: null });
                  if (row) navigate("/v2/csp/override", { state: { override: row } });
                }}
                sx={generateMenuSx(176, 37)}
              >
                <LayerdIcon icon={UI_ICONS.edit} size={18} color="neutral" />
                <Typography sx={{ fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                  Edit
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                  const row = routeOverrides.find((item) => item.id === routeOverrideActionMenu.rowId);
                  setRouteOverrideActionMenu({ anchorEl: null, rowId: null });
                  if (row) requestRouteOverrideDelete(row);
                }}
                sx={generateMenuSx(176, 37)}
              >
                <LayerdIcon icon={UI_ICONS.delete} size={18} color="neutral" />
                <Typography sx={{ fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                  Delete
                </Typography>
              </Stack>
            </Box>
          </Menu>

          {/* ── Card 4 — Phase 5: Violation intelligence ────────── */}
          {showIntel && (
          <SectionCard
            icon={<InsightsOutlinedIcon />}
            title="Violation intelligence"
            desc="Aggregates the raw browser reports into actionable clusters: what's blocked most, which pages generate them, and which look like real campaigns worth investigating vs. one-off noise."
            right={
              <Stack direction="row" spacing={1} alignItems="center">
                {[
                  { h: 24, label: "24h" },
                  { h: 168, label: "7d" },
                  { h: 720, label: "30d" },
                ].map((w) => (
                  <Chip
                    key={w.h}
                    label={w.label}
                    onClick={() => setIntelWindowHours(w.h)}
                    size="small"
                    sx={{
                      fontSize: 11.5,
                      height: 24,
                      fontWeight: 600,
                      cursor: "pointer",
                      background: intelWindowHours === w.h ? INDIGO_BG : NEUTRAL_BG,
                      color: intelWindowHours === w.h ? INDIGO : FG2,
                      border: `1px solid ${intelWindowHours === w.h ? "rgba(99,113,247,0.28)" : BORDER}`,
                    }}
                  />
                ))}
                <RefreshIconButton
                  onClick={loadIntel}
                  disabled={intelLoading}
                  loading={intelLoading}
                  size="compact"
                />
              </Stack>
            }
          >
            <ViolationIntelPanel data={intelData} loading={intelLoading} />
          </SectionCard>
          )}
          </Stack>
        )}
      </Stack>

      {/* Modals — mounted only when the owning section is visible so a
          dedicated /violations or /routes page doesn't accidentally
          own dialogs it can't reach. */}
      {showViol && (
        <ViolationDrawer
          open={!!drawerRow}
          row={drawerRow}
          onClose={() => setDrawerRow(null)}
          onOpenInInventory={(blockedUri) => {
            setDrawerRow(null);
            navigate(`/v2/csp/inventory?highlightUrl=${encodeURIComponent(blockedUri)}`);
          }}
        />
      )}
      {showPolicy && (
        <GeneratePolicyDialog
          open={generateDialogOpen}
          siteUid={site_uid}
          generateCandidatePolicy={generateCandidatePolicy}
          simulatePolicy={simulatePolicy}
          updateCspConfig={updateCspConfig}
          onClose={() => setGenerateDialogOpen(false)}
          onApplied={() => site_uid && getCspViolations(site_uid, { windowHours: 24, recentLimit: 50 })}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}
      {showPolicy && (
        <PolicyBuilderDialog
          open={builderDialogOpen}
          onClose={() => setBuilderDialogOpen(false)}
          onApply={(text) => {
            setDraft((d) => ({ ...d, policy_text: text }));
            showSuccess("Policy text updated — review then save to apply.");
          }}
        />
      )}

      {showPolicy && (
        <StickySaveBar
          open={dirty}
          modifiedCount={modifiedFields.length}
          modifiedFields={modifiedFields}
          onDiscard={handleDiscard}
          onApply={handleSave}
          isApplying={saving}
        />
      )}
      {showRoutes && (
        <DeleteConfirmModal
          open={Boolean(pendingRouteOverrideDelete)}
          onClose={() => setPendingRouteOverrideDelete(null)}
          onConfirm={executeRouteOverrideDelete}
          title="Delete route policy"
          loading={routeOverrideDeleting}
        >
          Are you sure you want to delete the route policy for{" "}
          <strong>{pendingRouteOverrideDelete?.path_pattern}</strong>? This action cannot be undone.
        </DeleteConfirmModal>
      )}
        </>
      )}
    </SiteScopeGate>
  );
}

export default CspConfig;
